import { Config, fetchJSON, Languages } from '@ratehub/base-ui';

import DEFAULT_CARD_PREFERENCES from '../definitions/DefaultCardPreferences';
import createCardInfoFromAPIResponse from './createCardInfoFromAPIResponse';


/**
 * Returns a CreditCardShape for the specified card.
 * Note: this is for a SINGLE card. There is also fetchCreditCards() for multiple.
 * @param {object} params - params object
 * @param {string} [params.slug] - the target card’s slug
 * @param {number} [params.id] - the target card’s id (only one of slug or id is required)
 * @param {object} [params.userPreferences=DEFAULT_CARD_PREFERENCES] - user preferences as collected by CardFinder, for example
 * @param {string} [params.language=Languages.ENGLISH] - 'en' | 'fr'
 * @param {string} [params.province] - the province code (e.g. 'ON')
 * @param {boolean} [params.isDraftMode] - returns the latest draft version of the card
 * @param {string} [params.profileId] - profile id from session store
 * @param {string} [params.documentId] - recent CC journey document id
 * @param {object} [abortControllerSignal] - an AbortSignal object instance to cancel ongoing fetch request
 * @returns {Promise<import('@ratehub/cc-common').CreditCardShape|undefined>} - the requested FE credit card or undefined
 */

async function fetchCreditCard(
    {
        slug,
        id,
        userPreferences = DEFAULT_CARD_PREFERENCES,
        language = Languages.ENGLISH,
        province,
        isDraftMode,
        profileId,
        documentId,
    },
    abortControllerSignal
) {
    const { monthlySpending } = userPreferences;

    const json = await fetchJSON({
        url: Config.CC_API_URL + '/card',
        options: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },

            body: JSON.stringify({
                id: slug || id,
                language,
                draftMode: isDraftMode,
                province,
                ...(monthlySpending && { spendingProfile: monthlySpending }),
                profileId,
                documentId,
            }),
        },
        signal: abortControllerSignal,
    }, 'fetchCreditCard');

    // success: json.data.creditCard is raw BE response
    // failure: json.data.creditCard === null

    if (!json?.data?.creditCard) {
        // Partnerships may leave cards hidden for product single on purpose for temporarily disabled cards.

        // There is no need for a `failSilently` argument here because we never call `noticeError()`.
        // When isDraftMode is true, even cards marked as hidden will be returned.

        // eslint-disable-next-line no-console
        console.error(
            `[fetchCreditCard] Could not fetch credit card “${slug || id}”.${isDraftMode
                ? ''
                : ' Is it marked as hidden?'}`,
            {
                slug,
                id,
                language,
                province,
                isDraftMode,
            }
        );

        return undefined;
    }


    return createCardInfoFromAPIResponse(
        json.data.creditCard,
        userPreferences,
        !!json.data.valid_approval_likelihood_document
    );
}

export default fetchCreditCard;
